'use client';

import { useEffect, useSyncExternalStore } from 'react';

/**
 * Page-level override for the Performance Lab navbar, which renders in the
 * shared chrome above the page and so can't read page context directly.
 * `flat`: dark text on the page background at the top, frosted only once scrolled.
 * `overlay`: white text straight over a full-bleed hero at the top, frosted once scrolled.
 * `flat-geist`: `flat`, with the white Geist "Book a callback" pill (festive V2 light hero).
 */
export type PerformanceLabNavAppearance =
  | 'default'
  | 'flat'
  | 'flat-geist'
  | 'overlay';

let current: PerformanceLabNavAppearance = 'default';
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

const setAppearance = (next: PerformanceLabNavAppearance) => {
  if (next === current) return;
  current = next;
  listeners.forEach((listener) => listener());
};

export const usePerformanceLabNavAppearance = () =>
  useSyncExternalStore(
    subscribe,
    () => current,
    () => 'default' as const,
  );

/** Switches the navbar to `appearance` while the calling page is mounted (null = leave it). */
export const usePerformanceLabNavOverride = (
  appearance: Exclude<PerformanceLabNavAppearance, 'default'> | null,
) => {
  useEffect(() => {
    if (!appearance) return undefined;
    setAppearance(appearance);
    return () => setAppearance('default');
  }, [appearance]);
};

/** Switches the navbar to `flat` while the calling page is mounted (and `enabled`). */
export const useFlatPerformanceLabNav = (enabled: boolean) =>
  usePerformanceLabNavOverride(enabled ? 'flat' : null);
